import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  TemplateRef,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  dateNavigatorWidget,
  dropdownWidgetRef,
  Menu,
  menuBar,
  menuBarButton,
  MenuBarButton,
  MenuBarDropdown,
  MenuBarDropdownContentDirective,
  MenuBarPicker,
  MenuBarPillSwitch,
  menuBarRef,
  MenuBarService,
  MenuBarSplitButton,
  menuBarTab,
  MenuItem,
  PickerOption,
  pickerWidgetRef,
  splitButtonRef,
} from '@softapps/core';
import {
  CalendarViewType,
  RosterContent,
  RosterService,
  RosterWithDepartmentHeadHostComponent,
} from '@softapps/pms/roster';
import {
  CheckboxComponent,
  DsContextMenuComponent,
  DsContextMenuDividerComponent,
  DsContextMenuItemComponent,
  IconComponent,
  InPipe,
  translator,
} from '@softline/ui-core';
import { GarageRosterService } from '../services/garage-roster.service';

@Component({
  selector: 'soft-softline-roster-menu-bar',
  templateUrl: './menu-bar.component.html',
  styles: `
    :host {
      display: contents;
    }
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CheckboxComponent,
    IconComponent,
    InPipe,
    Menu,
    MenuBarButton,
    MenuBarDropdown,
    MenuBarPicker,
    MenuBarPillSwitch,
    MenuBarSplitButton,
    MenuItem,
    PickerOption,
    MenuBarDropdownContentDirective,
    DsContextMenuComponent,
    DsContextMenuItemComponent,
    DsContextMenuDividerComponent,
  ],
})
export class GarageRosterMenuBarComponent {
  readonly service = inject(RosterService);
  readonly menuBarService = inject(MenuBarService);
  readonly hostComponent = inject(RosterWithDepartmentHeadHostComponent);

  /** Platform-correct modifier for keyboard-shortcut chips: ⌘ on macOS/iOS,
      Ctrl elsewhere. */
  readonly modifierKey = /mac|iphone|ipad|ipod/i.test(
    navigator.userAgent || navigator.platform || ''
  )
    ? '⌘'
    : 'Ctrl';

  readonly selectedEvents = computed(() => {
    return this.service.selectedEvents();
  });

  readonly viewTypeOptions = computed<{ v: CalendarViewType; l: string }[]>(() => [
    { v: 'day', l: 'Tag' },
    { v: 'workWeek', l: 'Arbeitswoche' },
    { v: 'week', l: 'Woche' },
    { v: 'month', l: 'Monat' },
    { v: 'planMonth', l: 'Planmonat' },
  ]);

  readonly viewTypeLabel = computed(
    () =>
      this.viewTypeOptions().find((o) => o.v === this.hostComponent.calendarViewType())
        ?.l ?? ''
  );

  readonly isAppointment = computed(() => {
    return (
      this.selectedEvents().length >= 1 &&
      this.selectedEvents().some((o: RosterContent) => {
        const details = o?.additionalData?.['additionaldetail'];
        if (!details || details.length < 1) return false;
        return details[0].tabname === 'ORDER' || details[0].tabname === 'ORDER';
      })
    );
  });

  readonly isOrderSelection = computed(() => {
    const events = this.selectedEvents();
    return (
      events.length >= 1 &&
      events.every((o: RosterContent) => {
        const tabname = o?.additionalData?.['additionaldetail']?.[0]?.tabname;
        return tabname === 'ORDER' || tabname === 'OBJECT';
      })
    );
  });

  readonly isTaskSelection = computed(() => {
    const events = this.selectedEvents();
    return (
      events.length >= 1 &&
      events.every((o: RosterContent) =>
        o?.additionalData?.['additionaldetail']?.some(
          (d) => d.tabname === 'GARAGE_TASK'
        )
      )
    );
  });

  readonly menuBar = menuBarRef({
    start: menuBarTab({
      selectedDate: dateNavigatorWidget({
        syncedDate: this.hostComponent.normalizedDate,
        interval: this.hostComponent.dateNavigatorInterval,
        disabled: signal(false),
      }),
      departmentHead: pickerWidgetRef({
        value: computed(() => this.hostComponent.departmentHead()),
        onChange: (value) => {
          if (!value) return;
          untracked(() => this.hostComponent.onDepartmentHeadChange(value));
        },
      }),
      create: splitButtonRef({
        execute: () => {
          this.service.onMenuItemExecute({
            value: signal('plan'),
          } as any);
          return false;
        },
        onMenuItemExecute: <T>(menuItem: MenuItem<T>) => {
          this.service.onMenuItemExecute(menuItem);
        },
      }),
    }),
    view: menuBarTab({
      interval: pickerWidgetRef({
        value: computed(() => this.hostComponent.calendarViewType()),
        onChange: (value) => {
          this.hostComponent.calendarViewType.set(value);
        },
      }),
      details: pickerWidgetRef({
        value: computed(() => this.hostComponent.calendarDetails()),
        onChange: (value) => {
          this.hostComponent.calendarDetails.set(value);
        },
      }),
      columnWidth: pickerWidgetRef({
        value: computed(() => this.hostComponent.calendarColumnWidth()),
        onChange: (value) => {
          this.hostComponent.calendarColumnWidth.set(value);
        },
      }),
      eventTypeFilter: dropdownWidgetRef({}),
    }),
    selected: menuBarTab({
      copy: menuBarButton({
        execute: () => {
          this.service.handleContextMenuAction('copy');
          return false;
        },
        disabled: computed(() => this.service.selectedEvents().length > 1),
      }),
      duplicate: menuBarButton({
        execute: () => {
          this.service.handleContextMenuAction('duplicate');
          return false;
        },
      }),
      delete: menuBarButton({
        execute: () => {
          this.service.handleContextMenuAction('delete');
          return true;
        },
      }),
      edit: menuBarButton({
        execute: () => {
          this.service.handleContextMenuAction('edit');
          return false;
        },
        disabled: computed(() => this.service.selectedEvents().length > 1),
      }),
    }),
  });

  readonly viewLeadingMenuBarTemplate = viewChild.required('viewLeadingMenuBar', {
    read: TemplateRef,
  });

  readonly selectionLeadingMenuBarTemplate = viewChild.required(
    'selectionLeadingMenuBar',
    {
      read: TemplateRef,
    }
  );
  readonly selectionTrailingMenuBarTemplate = viewChild.required(
    'selectionTrailingMenuBar',
    {
      read: TemplateRef,
    }
  );

  readonly garageEventMenuTemplate = viewChild.required('garageEventMenu', {
    read: TemplateRef,
  });

  readonly t = translator();

  constructor() {
    this.hostComponent.calendarDetails.set('all');

    effect(() => {
      (this.service as GarageRosterService).garageEventMenu.set(
        this.garageEventMenuTemplate()
      );
    });

    menuBar({
      key: 'pms/roster/view',
      title: this.t('Ansicht'),
      template: this.viewLeadingMenuBarTemplate,
      separated: true,
      align: 'leading',
      order: 2,
    });

    menuBar({
      key: 'garage/roster/selection',
      title: this.t('Auswahl'),
      template: this.selectionLeadingMenuBarTemplate,
      separated: true,
      visible: computed(() => this.selectedEvents().length > 0),
      order: 3,
    });

    menuBar({
      key: 'garage/roster/selection',
      title: this.t('Auswahl'),
      template: this.selectionTrailingMenuBarTemplate,
      separated: true,
      visible: computed(() => this.selectedEvents().length > 0),
      align: 'trailing',
      order: 3,
    });

    effect(() => {
      const selectedEvents = this.selectedEvents();
      if (selectedEvents.length > 0) {
        untracked(() => {
          this.menuBarService.activateKey('garage/roster/selection');
        });
      } else if (selectedEvents.length === 0) {
        untracked(() => {
          this.menuBarService.activateIndex(0);
        });
      }
    });
  }
}
